import { NavLink, useLocation } from 'react-router-dom'
import {
  Calendar, LayoutDashboard, Server, Ticket, Bell, BellRing, Bot, UserCheck,
  Settings, ChevronLeft, ChevronRight, ChevronDown, ShieldCheck, Building2,
  Activity, ClipboardList, Wrench, FlaskConical, Gauge, Bug, Rocket, GitBranch, HardDrive, Cpu, Sparkles, Terminal, HeartPulse,
} from 'lucide-react'
import { useState, type ComponentType } from 'react'
import { cn } from '@/lib/utils'

interface NavItem { path: string; icon: ComponentType<{ className?: string }>; label: string }

const mainItems: NavItem[] = [
  { path: '/', icon: LayoutDashboard, label: '仪表盘' },
  { path: '/assets', icon: Server, label: '资产管理' },
  { path: '/tickets', icon: Ticket, label: '工单中心' },
  { path: '/duty', icon: Calendar, label: '值班表' },
  { path: '/monitor', icon: Bell, label: '监控告警' },
  { path: '/notifications', icon: BellRing, label: '通知中心' },
  { path: '/leave', icon: UserCheck, label: '请假中心' },
  { path: '/org', icon: Building2, label: '组织' },
  { path: '/permissions', icon: ShieldCheck, label: '权限矩阵' },
  { path: '/ai', icon: Bot, label: 'AI 助手' },
]

// 运维组（挂账148 前台化：一切能可视化的都往前台搬——11 类后台操作的页面门牌）
const opsItems: NavItem[] = [
  { path: '/ops', icon: Activity, label: '系统健康' },
  { path: '/ops/charges', icon: ClipboardList, label: '挂账台账' },
  { path: '/ops/mechanisms', icon: Wrench, label: '机制' },
  { path: '/ops/smoke', icon: FlaskConical, label: '烟验' },
  { path: '/ops/evo', icon: HeartPulse, label: 'AI 体检' },
  { path: '/ops/metrics', icon: Gauge, label: '度量' },
  { path: '/ops/pitfalls', icon: Bug, label: '坑表' },
  { path: '/ops/release', icon: Rocket, label: '发版' },
  { path: '/ops/git', icon: GitBranch, label: 'Git' },
  { path: '/ops/backup', icon: HardDrive, label: '备份' },
  { path: '/ops/a10', icon: Cpu, label: 'A10' },
  { path: '/ops/ai', icon: Sparkles, label: 'AI 运维' },
  { path: '/ops/system', icon: Terminal, label: '系统面' },
]

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation()
  const Icon = item.icon
  const isActive = location.pathname === item.path
  return (
    <NavLink
      to={item.path}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-apple-blue/10 text-apple-blue'
          : 'text-apple-gray-500 hover:text-apple-gray-900 hover:bg-apple-gray-50'
      )}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  )
}

export default function Sidebar() {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(false)
  const [opsOpen, setOpsOpen] = useState(location.pathname.startsWith('/ops'))
  const opsActive = location.pathname.startsWith('/ops')

  return (
    <aside
      className={cn(
        'bg-white border-r border-apple-gray-100 flex flex-col transition-all duration-300',
        collapsed ? 'w-20' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-5 border-b border-apple-gray-100">
        <div className="w-8 h-8 bg-gradient-to-br from-apple-blue to-apple-purple rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">G</span>
        </div>
        {!collapsed && (
          <span className="ml-3 font-semibold text-lg tracking-tight text-apple-gray-900">GreatOps</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-auto">
        {mainItems.map((item) => <NavRow key={item.path} item={item} collapsed={collapsed} />)}
        <button
          onClick={() => setOpsOpen(!opsOpen)}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
            opsActive
              ? 'bg-apple-blue/10 text-apple-blue'
              : 'text-apple-gray-500 hover:text-apple-gray-900 hover:bg-apple-gray-50'
          )}
        >
          <Wrench className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="flex-1 text-left">运维</span>}
          {!collapsed && (opsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
        </button>
        {opsOpen && (
          <div className="ml-4 pl-2 border-l border-apple-gray-100 space-y-1">
            {opsItems.map((item) => <NavRow key={item.path} item={item} collapsed={collapsed} />)}
          </div>
        )}
      </nav>

      {/* Bottom Actions */}
      <div className="p-3 border-t border-apple-gray-100 space-y-1">
        <NavRow item={{ path: '/settings', icon: Settings, label: '系统设置' }} collapsed={collapsed} />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center px-3 py-3 rounded-xl text-apple-gray-400 hover:text-apple-gray-600 hover:bg-apple-gray-50 transition-all"
        >
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </div>
    </aside>
  )
}
